import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { AuthImage } from "@/components/AuthMedia";
import { AlertTriangle, Factory, Ban, Clock, Link2, Users, Car, Trophy } from "lucide-react";
import { DataCompletenessPanel, MyKRA } from "@/pages/admin/AdminWidgets";

export default function DirectorDashboard() {
  const { t } = useLang();
  const [k, setK] = useState(null);
  const [league, setLeague] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [portal, setPortal] = useState(null);
  const [inFactory, setInFactory] = useState(null);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setK(r.data));
    api.get("/dashboard/inspector-league").then((r) => setLeague(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/portal/metrics").then((r) => setPortal(r.data)).catch(() => setPortal(null));
    const loadIn = () => api.get("/visitors/current").then((r) => setInFactory(r.data)).catch(() => setInFactory(null));
    loadIn();
    const timer = setInterval(loadIn, 30000);
    return () => clearInterval(timer);
  }, []);

  if (!k) return <div>{t("loading")}</div>;

  return (
    <div>
      <div className="eyebrow">{t("nav_dashboard")}</div>
      <h1 className="text-3xl font-bold" style={{ color: "var(--brand-navy)" }}>Director Overview</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        <KPI label={t("active_looms")} v={k.active_looms} sub={`${k.total_looms} total`} icon={<Factory size={16} />} />
        <KPI label={t("on_time")} v={`${k.on_time_pct}%`} sub={t("on_time")} />
        <KPI label={t("open_escalations")} v={k.open_escalations} sub={`${k.critical_escalations} ${t("critical")}`} icon={<AlertTriangle size={16} />} tone={k.critical_escalations > 0 ? "red" : ""} />
        <KPI label={t("pending_inspector_assignment")} v={k.pending_assignment + k.missing_eta_pos} sub={`${k.missing_eta_pos} missing ETA`} tone="amber" icon={<Clock size={16} />} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div className="md:col-span-1">
          <MyKRA />
        </div>
        <div className="md:col-span-2 hag-surface p-4 flex items-center justify-between" data-testid="director-kra-cta">
          <div>
            <div className="eyebrow flex items-center gap-1"><Trophy size={12} /> KRA Leaderboard</div>
            <div className="text-sm text-[#4A627A] mt-1">Automated 30-day performance scoring for every role.</div>
          </div>
          <Link to="/admin/kra-leaderboard" data-testid="director-kra-link"
            className="text-xs px-3 py-1.5 rounded-full border border-[#1B3A5C]/20 hover:bg-[#FAF6F0] font-semibold"
            style={{ color: "var(--brand-navy)" }}>
            View leaderboard →
          </Link>
        </div>
      </div>

      <WhoIsInPanel data={inFactory} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="hag-surface p-5">
          <div className="eyebrow">Loom grades</div>
          <div className="flex gap-3 mt-3">
            <GradeBar label="A" count={k.grade_counts.A} color="#16A34A" />
            <GradeBar label="B" count={k.grade_counts.B} color="#D97706" />
            <GradeBar label="C" count={k.grade_counts.C} color="#DC2626" />
          </div>
        </div>
        <div className="hag-surface p-5">
          <div className="eyebrow">LSD order book</div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <MiniStat label={t("lsd_outstanding")} v={k.lsd_total_outstanding} />
            <MiniStat label={t("lsd_overdue")} v={k.lsd_red} tone="red" />
            <MiniStat label={t("lsd_due_soon")} v={k.lsd_amber} tone="amber" />
          </div>
          {k.lsd_unallocated > 0 && (
            <Link to="/supervisor/unallocated" className="mt-3 inline-flex items-center gap-2 text-red-700 text-sm">
              <Ban size={14} /> {k.lsd_unallocated} unallocated vendor lines
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="hag-surface p-5">
          <div className="eyebrow">{t("top_delayed")}</div>
          <div className="mt-3 space-y-2">
            {k.top_delayed_pos.map((p) => (
              <Link key={p.id} to={`/po/${p.id}`} data-testid={`delayed-${p.po_number}`} className="flex items-center justify-between p-2 rounded-md hover:bg-[#FAF6F0]">
                <span className="font-semibold text-[#1B3A5C]">{p.po_number}</span>
                <span className="text-xs text-[#7C8E9E]">ETA {p.eta ? new Date(p.eta).toLocaleDateString() : "—"} · {p.percent_complete}%</span>
                <span className={`status-chip pace-${p.pace_flag}`}>{p.pace_flag}</span>
              </Link>
            ))}
            {k.top_delayed_pos.length === 0 && <div className="text-sm text-[#7C8E9E]">No red-pace POs.</div>}
          </div>
        </div>

        <div className="hag-surface p-5">
          <div className="eyebrow">{t("inspector_league")}</div>
          <div className="mt-3">
            <table className="w-full text-sm">
              <thead className="text-[#7C8E9E] uppercase text-xs tracking-widest">
                <tr><th className="text-left py-2">Name</th><th className="text-right">Inspections</th><th className="text-right">Defects</th><th className="text-right">A-grades</th></tr>
              </thead>
              <tbody>
                {league.map((i) => (
                  <tr key={i.id} className="border-t border-[#1B3A5C]/5">
                    <td className="py-2">{i.name}</td>
                    <td className="text-right">{i.inspections}</td>
                    <td className="text-right">{i.defects_caught}</td>
                    <td className="text-right">{i.a_grades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="hag-surface p-5 mt-6">
        <div className="eyebrow">Vendors</div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          {vendors.map((v) => (
            <Link key={v.id} to={`/director/vendor/${v.id}`} data-testid={`vendor-card-${v.name}`} className="p-3 border border-[#1B3A5C]/10 rounded-md hover:bg-[#FAF6F0]">
              <div className="font-bold">{v.name}</div>
              <div className="text-xs text-[#7C8E9E]">{v.type} · {v.active_lines_count} lines</div>
            </Link>
          ))}
        </div>
      </div>

      <PortalMetricsTile portal={portal} />

      <DataCompletenessPanel />
    </div>
  );
}

function PortalMetricsTile({ portal }) {
  if (!portal) return null;
  const { totals, rows } = portal;
  const top = rows.slice(0, 6);
  return (
    <div className="hag-surface p-5 mt-6" data-testid="director-portal-metrics">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow flex items-center gap-1"><Link2 size={12} /> Vendor Portal</div>
          <div className="text-sm text-[#7C8E9E]">Magic-link engagement — last 7 &amp; 28 days</div>
        </div>
        <Link to="/director/portal-tokens" className="text-xs font-semibold text-[#1B3A5C] hover:underline" data-testid="portal-metrics-manage">
          Manage links →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        <MiniStat label="Updates · 7d" v={totals.updates_last_7d} />
        <MiniStat label="Updates · 28d" v={totals.updates_last_28d} />
        <MiniStat label="Active vendors · 7d" v={totals.vendors_active_7d} />
        <MiniStat label="Silent ≥14d" v={totals.vendors_silent_14d} tone={totals.vendors_silent_14d > 0 ? "amber" : ""} />
        <MiniStat label="Active tokens" v={totals.tokens_active} />
      </div>

      {top.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">
                <th className="text-left py-2">Vendor</th>
                <th className="text-right">7d</th>
                <th className="text-right">28d</th>
                <th className="text-right">All-time</th>
                <th className="text-right">Days silent</th>
                <th className="text-left pl-3">Token</th>
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <tr key={r.vendor_id} className="border-t border-[#1B3A5C]/5" data-testid={`portal-metric-row-${r.vendor_id}`}>
                  <td className="py-2 font-semibold">{r.vendor_name}</td>
                  <td className="text-right">{r.updates_last_7d}</td>
                  <td className="text-right">{r.updates_last_28d}</td>
                  <td className="text-right">{r.updates_total}</td>
                  <td className="text-right">
                    {r.days_since_last_update === null
                      ? <span className="text-[#7C8E9E]">—</span>
                      : r.days_since_last_update >= 14
                        ? <span className="text-red-700 font-semibold">{r.days_since_last_update}</span>
                        : <span>{r.days_since_last_update}</span>}
                  </td>
                  <td className="pl-3">
                    {r.token_status === "active"   && <span className="status-chip pace-green">Active</span>}
                    {r.token_status === "expired"  && <span className="status-chip pace-amber">Expired</span>}
                    {r.token_status === "revoked"  && <span className="status-chip pace-red">Revoked</span>}
                    {r.token_status === "none"     && <span className="status-chip pace-none">No link</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {top.length === 0 && (
        <div className="mt-4 text-xs text-[#7C8E9E]">No portal activity yet. Issue a magic link from <Link className="underline" to="/director/portal-tokens">Vendor Portal Links</Link>.</div>
      )}
    </div>
  );
}

function KPI({ label, v, sub, tone, icon }) {
  const border = tone === "red" ? "border-red-400" : tone === "amber" ? "border-amber-400" : "border-[#1B3A5C]/10";
  return (
    <div className={`hag-surface p-4 border-l-4 ${border}`}>
      <div className="text-xs uppercase tracking-widest text-[#7C8E9E] flex items-center gap-1">{icon}{label}</div>
      <div className="text-3xl font-black mt-1" style={{ color: "var(--brand-navy)" }}>{v}</div>
      {sub && <div className="text-xs text-[#7C8E9E] mt-1">{sub}</div>}
    </div>
  );
}

function GradeBar({ label, count, color }) {
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-bold" style={{ color }}>{label}</span>
        <span className="text-[#4A627A]">{count}</span>
      </div>
      <div className="h-2 rounded-full mt-1" style={{ background: color, opacity: 0.7 }} />
    </div>
  );
}

function MiniStat({ label, v, tone }) {
  const cls = tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "text-[#1B3A5C]";
  return (
    <div className="p-2">
      <div className={`text-2xl font-black ${cls}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{label}</div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Who is in the factory now?  Live panel refreshed every 30s.
// -------------------------------------------------------------------------
function WhoIsInPanel({ data }) {
  const list = data?.visitors || [];
  const count = data?.count ?? 0;

  return (
    <div className="hag-surface p-5 mt-6" data-testid="who-is-in-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Users size={12} /> Who’s in the factory now
          </div>
          <h2 className="text-xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>
            {count === 0
              ? "No visitors currently on premises"
              : `${count} visitor${count === 1 ? "" : "s"} on premises`}
          </h2>
        </div>
        <Link to="/gate/visitors"
          data-testid="who-is-in-log-link"
          className="text-xs px-3 py-1.5 rounded-full border border-[#1B3A5C]/20 hover:bg-[#FAF6F0]"
          style={{ color: "var(--brand-navy)" }}>
          Open visitor log →
        </Link>
      </div>

      {list.length === 0 ? (
        <div className="text-xs text-[#7C8E9E] italic">
          The gate will show visitors here the moment they check in.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.slice(0, 6).map(v => <WhoIsInCard key={v.id} v={v} />)}
        </div>
      )}

      {list.length > 6 && (
        <div className="mt-2 text-xs text-[#7C8E9E]">
          + {list.length - 6} more · <Link to="/gate/visitors" className="underline">see all</Link>
        </div>
      )}

      {data?.as_of && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-[#B0B7BF]">
          Last refresh {new Date(data.as_of).toLocaleTimeString()} · auto-updates every 30s
        </div>
      )}
    </div>
  );
}

function WhoIsInCard({ v }) {
  return (
    <div className="border border-[#1B3A5C]/10 rounded-lg p-3 bg-white flex gap-3"
      data-testid={`who-in-${v.visitor_no}`}>
      {v.visitor_photo_file_id ? (
        <AuthImage fileId={v.visitor_photo_file_id}
          alt={v.name}
          className="w-12 h-12 rounded-full object-cover ring-2 ring-[#B08B49]/40 flex-shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-[#FAF6F0] flex items-center justify-center flex-shrink-0">
          <Users size={18} className="text-[#B08B49]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm truncate" style={{ color: "var(--brand-navy)" }}>
          {v.name}
        </div>
        <div className="text-[11px] text-[#4A627A] truncate">
          {v.purpose || "—"}
          {v.host_name ? ` · ${v.host_name}` : ""}
        </div>
        <div className="flex items-center gap-2 mt-1 text-[10px] text-[#7C8E9E]">
          <span>In {(v.in_time || "").slice(11, 16)}</span>
          {v.vehicle_no && (
            <span className="inline-flex items-center gap-1 font-mono uppercase">
              <Car size={10} /> {v.vehicle_no}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
